// Organization Users Tab - Lists all admin users in the organization
import React from 'react';
import { View, Text, StyleSheet, ScrollView, ActivityIndicator } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';

interface Admin {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}

interface OrganizationUsersTabProps {
  organizationId: number;
  admins: Admin[];
  isLoading: boolean;
}

export const OrganizationUsersTab: React.FC<OrganizationUsersTabProps> = ({
  organizationId,
  admins,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={ADMIN_COLORS.BUTTON.ADD_BUTTON_BACKGROUND} />
        <Text style={styles.loadingText}>Loading users...</Text>
      </View>
    );
  }

  if (!admins || admins.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <SVG_COMPONENTS.ADMIN width={48} height={48} />
        <Text style={styles.emptyTitle}>No Users</Text>
        <Text style={styles.emptyMessage}>
          This organization doesn't have any admin users assigned yet.
        </Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Admin Users</Text>
        <Text style={styles.sectionSubtitle}>
          {admins.length} {admins.length === 1 ? 'user' : 'users'} with access to this organization
        </Text>

        <View style={styles.listContainer}>
          {admins.map((admin) => (
            <View key={admin.id} style={styles.userItem}>
              <View style={styles.userIconContainer}>
                <SVG_COMPONENTS.ADMIN width={20} height={20} color={ADMIN_COLORS.TEXT.SECONDARY} />
              </View>
              <View style={styles.userInfo}>
                <Text style={styles.userName}>
                  {admin.firstName} {admin.lastName}
                </Text>
                <Text style={styles.userEmail}>{admin.email}</Text>
              </View>
            </View>
          ))}
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 20,
  },
  section: {
    paddingVertical: 20,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
    marginBottom: 4,
  },
  sectionSubtitle: {
    fontSize: 13,
    color: ADMIN_COLORS.TEXT.SECONDARY,
    marginBottom: 20,
  },
  listContainer: {
    gap: 8,
  },
  userItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
  },
  userIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  userInfo: {
    flex: 1,
  },
  userName: {
    fontSize: 14,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT.PRIMARY,
    marginBottom: 2,
  },
  userEmail: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: ADMIN_COLORS.TEXT.SECONDARY,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
    marginTop: 16,
    marginBottom: 8,
  },
  emptyMessage: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT.SECONDARY,
    textAlign: 'center',
  },
});

